import { useState, ReactNode } from "react";
import { useAppDispatch } from "@store/hooks";
import { useNavigate } from "react-router-dom";
import { Updater, useImmer } from "use-immer";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Slide from "@mui/material/Slide";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import {
  ChevronLeft,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";
import Paper from "@mui/material/Paper";

import logo from "@/shared/assets/logo.png";

interface Props {
  modals: (
    modalVisible: string,
    setModalVisible: Updater<string>
  ) => ReactNode[];
}

const drawerWidth = 240;

export default function MainNavbar({ modals }: Props) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const [modalVisible, setModalVisible] = useImmer("");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const trigger = useScrollTrigger({ threshold: 80 }); // Adjust threshold as needed

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const drawer = (
    <Box sx={{ textAlign: "center" }}>
      <Box
        sx={{
          background: theme.palette.primary.main,
          marginTop: -1,
        }}
      >
        <IconButton onClick={handleDrawerToggle} sx={{ float: "right" }}>
          <ChevronLeft />
        </IconButton>
        <Typography
          onClick={() => navigate("/")}
          variant="h5"
          sx={{ my: 1, paddingTop: 5 }}
        >
          AnnoGram
        </Typography>
      </Box>
      <Divider />
      <List onClick={() => handleDrawerToggle()}>
        <ListItem disablePadding>
          <ListItemButton
            sx={{
              textAlign: "center",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            onClick={() => navigate("/")}
          >
            <ListItemText primary="Home" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            sx={{
              textAlign: "center",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            onClick={() => {
              setModalVisible("login");
            }}
          >
            <ListItemText primary="Login" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            sx={{
              textAlign: "center",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
            onClick={() => {
              setModalVisible("register");
            }}
          >
            <ListItemText primary="Register" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  // Floating mini-navbar for scroll
  const floatingNav = (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        borderRadius: 4,
        px: 3,
        py: 1,
        zIndex: theme.zIndex.appBar + 2,
        display: "flex",
        alignItems: "center",
        bgcolor: theme.palette.background.paper,
        boxShadow: 6,
        minWidth: 280,
      }}
    >
      <IconButton onClick={() => navigate("/")}>
        <Box
          component="img"
          alt="logo"
          src={logo}
          height="28px"
          width="28px"
          borderRadius="25%"
          sx={{
            objectFit: "cover",
            // Logo is dark-gray on transparent. In dark mode it
            // disappears into the navbar; inverting flips the dark
            // pixels to light without affecting transparent ones.
            filter: (t) =>
              t.palette.mode === "dark" ? "invert(1)" : "none",
          }}
        />
      </IconButton>
      <Button
        sx={{
          color: theme.palette.text.primary,
          mx: 1,
          fontSize: "0.95rem",
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
        onClick={() => navigate("/")}
      >
        Home
      </Button>
      <Button
        sx={{
          color: theme.palette.text.primary,
          mx: 1,
          fontSize: "0.95rem",
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
        onClick={() => setModalVisible("login")}
      >
        Login
      </Button>
      <Button
        sx={{
          color: theme.palette.text.primary,
          mx: 1,
          fontSize: "0.95rem",
          "&:hover": { backgroundColor: theme.palette.action.hover },
        }}
        onClick={() => setModalVisible("register")}
      >
        Register
      </Button>
    </Paper>
  );

  return (
    <Box sx={{ display: "flex" }}>
      {/* Main AppBar: only show when not scrolled */}
      <Slide appear={false} direction="down" in={!trigger}>
        <AppBar
          component="nav"
          sx={{
            boxShadow: "none",
            position: "static", // Not fixed
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }}>
              <IconButton onClick={() => navigate("/")}>
                <Box
                  component="img"
                  alt="logo"
                  src={logo}
                  height="32px"
                  width="32px"
                  borderRadius="25%"
                  sx={{
                    objectFit: "cover",
                    filter: (t) =>
                      t.palette.mode === "dark" ? "invert(1)" : "none",
                  }}
                />
              </IconButton>
            </Box>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Button
                sx={{
                  color: theme.palette.text.primary,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                onClick={() => navigate("/")}
              >
                Home
              </Button>
              <Button
                sx={{
                  color: theme.palette.text.primary,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                onClick={() => {
                  setModalVisible("login");
                }}
              >
                Login
              </Button>
              <Button
                sx={{
                  color: theme.palette.text.primary,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                onClick={() => {
                  setModalVisible("register");
                }}
              >
                Register
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
      </Slide>
      {/* Floating navbar: only show when scrolled */}
      {trigger && (
        <Box sx={{ display: { xs: "none", sm: "flex" } }}>{floatingNav}</Box>
      )}
      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      {modals(modalVisible, setModalVisible)}
    </Box>
  );
}
